import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  EmptyState,
  PageHeader,
  SeverityBadge,
  Spinner,
} from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { getOrgIncidents } from '../lib/api';

/**
 * /o/:orgSlug/incidents — Org-tier cross-workspace incidents list.
 *
 * V3-8-6: Aggregates non-privileged incidents across all child
 * workspaces of the organization. Auth: organizationContext() on the
 * server side. Legal-privileged incidents are excluded at the API layer.
 */

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function mapSeverity(s: string): 'critical' | 'high' | 'elevated' | 'moderate' | 'standard' {
  const map: Record<string, 'critical' | 'high' | 'elevated' | 'moderate' | 'standard'> = {
    critical: 'critical',
    high: 'high',
    medium: 'moderate',
    low: 'standard',
  };
  return map[s] ?? 'standard';
}

function statusColor(s: string): string {
  switch (s) {
    case 'open':
      return 'var(--status-red)';
    case 'investigating':
    case 'contained':
      return 'var(--status-amber)';
    case 'resolved':
    case 'closed':
      return 'var(--success)';
    default:
      return 'var(--text-muted)';
  }
}

function statusLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function OrgIncidents() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();

  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const dataQuery = useQuery({
    queryKey: queryKeys.orgIncidents(orgSlug ?? ''),
    queryFn: () => getOrgIncidents(orgSlug!),
    enabled: !!orgSlug,
  });
  const data = {
    data: dataQuery.data ?? null,
    error: dataQuery.error?.message ?? null,
    loading: dataQuery.isLoading,
  };

  const filtered = useMemo(() => {
    if (!data.data?.incidents) return [];
    let items = data.data.incidents;
    if (severityFilter !== 'all') {
      items = items.filter((inc) => inc.severity === severityFilter);
    }
    if (statusFilter !== 'all') {
      items = items.filter((inc) => inc.status === statusFilter);
    }
    return items;
  }, [data.data, severityFilter, statusFilter]);

  if (data.loading) return <Spinner label="Loading incidents" />;
  if (data.error) return <Alert variant="error">{data.error}</Alert>;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Incidents"
        subtitle={`${data.data?.total ?? 0} incident${(data.data?.total ?? 0) === 1 ? '' : 's'} across all workspaces`}
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text-muted)',
          }}
        >
          Severity
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            style={{
              fontSize: 'var(--font-size-2xs)',
              padding: '4px 8px',
              borderRadius: 'var(--radius-chip)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            <option value="all">All</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text-muted)',
          }}
        >
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              fontSize: 'var(--font-size-2xs)',
              padding: '4px 8px',
              borderRadius: 'var(--radius-chip)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="investigating">Investigating</option>
            <option value="contained">Contained</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </label>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState
          title="No incidents"
          description="No incidents reported across your workspaces."
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-2xs)',
              color: 'var(--text)',
            }}
          >
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={thStyle}>Severity</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Title</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Status</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Workspace</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Obligations</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Reported</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inc) => (
                <tr
                  key={inc.incident_id}
                  onClick={() =>
                    inc.workspace_slug &&
                    navigate(`/w/${inc.workspace_slug}/app/incidents`)
                  }
                  style={{
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  <td style={tdStyle}>
                    <SeverityBadge severity={mapSeverity(inc.severity)} />
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 500, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {inc.title}
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        color: statusColor(inc.status),
                        fontWeight: 500,
                        fontSize: 'var(--font-size-3xs)',
                      }}
                    >
                      {statusLabel(inc.status)}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <Badge tone="neutral">{inc.workspace_name || inc.workspace_slug}</Badge>
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      textAlign: 'right',
                      fontFamily: 'var(--font-mono)',
                      color: inc.obligations_count > 0 ? 'var(--status-amber)' : 'var(--text-muted)',
                    }}
                  >
                    {inc.obligations_count}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                    {formatDate(inc.reported_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared cell styles                                                 */
/* ------------------------------------------------------------------ */

const thStyle: React.CSSProperties = {
  padding: 'var(--space-2)',
  color: 'var(--text-muted)',
  fontWeight: 500,
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--space-2)',
  whiteSpace: 'nowrap',
};
